import { IconUserPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    ProjectInvitationModel,
    ProjectMemberModel,
    ProjectModel,
} from '@xver/apis/v1/projects/model';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import {
    invalidateProjectQueries,
    useProjectFeedback,
    useProjectInvitationsQuery,
    useProjectMembersQuery,
} from '@xver/hooks/useProjectQueries';
import { canManageMember, focusAfterRender, memberLabel } from '@xver/utils/projects';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessSummary, DateTime, TextBadge } from '../../ProjectsPage/components/ProjectBits';
import MemberAccessModal, { MemberAccessTarget } from './MemberAccessModal';

interface MembersTabPanelProps {
    project: ProjectModel;
    viewerId: number | undefined;
}

export default function MembersTabPanel({ project, viewerId }: MembersTabPanelProps) {
    const { t } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { notifySuccess, notifyError } = useProjectFeedback();
    const canManage = project.role === 'owner' || project.role === 'admin';
    const members = useProjectMembersQuery(project.id);
    const invitations = useProjectInvitationsQuery(project.id, canManage);
    const [accessTarget, setAccessTarget] = useState<MemberAccessTarget | null>(null);
    const [removing, setRemoving] = useState<ProjectMemberModel | null>(null);
    const [revoking, setRevoking] = useState<ProjectInvitationModel | null>(null);
    const inviteButtonId = useId();
    const focusInvite = () => focusAfterRender(() => document.getElementById(inviteButtonId));

    const remove = useMutation({
        mutationFn: (member: ProjectMemberModel) =>
            Endpoints.v1.Projects.removeMember(project.id, member.userId),
        onSuccess: async (_, member) => {
            setRemoving(null);
            notifySuccess(
                t('members.toast.removedTitle'),
                t('members.toast.removedMessage', { name: memberLabel(member) })
            );
            await invalidateProjectQueries(queryClient);
            focusInvite();
        },
        onError: notifyError,
    });

    const revoke = useMutation({
        mutationFn: (invitation: ProjectInvitationModel) =>
            Endpoints.v1.Projects.revokeInvitation(project.id, invitation.id),
        onSuccess: async (_, invitation) => {
            setRevoking(null);
            notifySuccess(
                t('members.toast.revokedTitle'),
                t('members.toast.revokedMessage', { email: invitation.email })
            );
            await invalidateProjectQueries(queryClient);
            focusInvite();
        },
        onError: notifyError,
    });

    const memberColumns = useMemo<DataTableColumn<ProjectMemberModel>[]>(
        () => [
            {
                key: 'name',
                label: t('members.columns.name'),
                render: (member) => (
                    <div className="flex flex-col min-w-0">
                        <span className="font-medium break-words">
                            {memberLabel(member)}
                            {member.userId === viewerId && ` ${t('members.you')}`}
                        </span>
                        {member.name && member.email && (
                            <span className="text-xs text-fg/68 break-all">{member.email}</span>
                        )}
                    </div>
                ),
            },
            {
                key: 'role',
                label: t('members.columns.role'),
                render: (member) => (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {t(`roles.${member.role}`)}
                        {member.isPayer && <TextBadge>{t('members.payer')}</TextBadge>}
                    </div>
                ),
            },
            {
                key: 'access',
                label: t('members.columns.access'),
                render: (member) => <AccessSummary role={member.role} access={member.access} />,
            },
            {
                key: 'joined',
                label: t('members.columns.joined'),
                render: (member) => <DateTime iso={member.joinedAt} />,
            },
        ],
        [t, viewerId]
    );

    const invitationColumns = useMemo<DataTableColumn<ProjectInvitationModel>[]>(
        () => [
            {
                key: 'email',
                label: t('members.columns.invitee'),
                render: (invitation) => (
                    <div className="flex flex-col min-w-0">
                        <span className="font-medium break-all">{invitation.email}</span>
                        {invitation.name && (
                            <span className="text-xs text-fg/68">{invitation.name}</span>
                        )}
                    </div>
                ),
            },
            {
                key: 'role',
                label: t('members.columns.role'),
                render: (invitation) => t(`roles.${invitation.role}`),
            },
            {
                key: 'access',
                label: t('members.columns.access'),
                render: (invitation) => (
                    <AccessSummary role={invitation.role} access={invitation.access} />
                ),
            },
            {
                key: 'expires',
                label: t('members.columns.expires'),
                render: (invitation) => <DateTime iso={invitation.expiresAt} />,
            },
        ],
        [t]
    );

    return (
        <div className="w-full flex flex-col gap-6">
            <DialogBox
                label={t('members.title')}
                description={t('members.description')}
                rightDescription={
                    canManage && (
                        <Button
                            id={inviteButtonId}
                            size="lg"
                            leftIcon={<IconUserPlus size={16} aria-hidden="true" />}
                            onClick={() => setAccessTarget({ kind: 'invite' })}
                        >
                            {t('members.invite')}
                        </Button>
                    )
                }
                classNames="p-0"
            >
                {members.isError ? (
                    <LoadError onRetry={() => members.refetch()} />
                ) : (
                    <DataTable
                        caption={t('members.caption', { name: project.name })}
                        columns={memberColumns}
                        rows={members.data}
                        isLoading={members.isPending}
                        rowKey={(member) => member.userId}
                        emptyMessage={t('members.empty')}
                        actionsLabel={t('common.actions')}
                        actions={(member) =>
                            canManageMember(project.role, member.role) &&
                            member.userId !== viewerId ? (
                                <div className="flex flex-wrap justify-end gap-2">
                                    <Button
                                        size="lg"
                                        variant="secondary"
                                        onClick={() => setAccessTarget({ kind: 'edit', member })}
                                    >
                                        {t('members.edit')}
                                        <span className="sr-only"> {memberLabel(member)}</span>
                                    </Button>
                                    <Button
                                        size="lg"
                                        variant="danger"
                                        onClick={() => setRemoving(member)}
                                    >
                                        {t('members.remove')}
                                        <span className="sr-only"> {memberLabel(member)}</span>
                                    </Button>
                                </div>
                            ) : null
                        }
                    />
                )}
            </DialogBox>

            {canManage && (
                <DialogBox
                    label={t('members.invitationsTitle')}
                    description={t('members.invitationsDescription')}
                    classNames="p-0"
                >
                    {invitations.isError ? (
                        <LoadError onRetry={() => invitations.refetch()} />
                    ) : (
                        <DataTable
                            caption={t('members.invitationsCaption')}
                            columns={invitationColumns}
                            rows={invitations.data}
                            isLoading={invitations.isPending}
                            rowKey={(invitation) => invitation.id}
                            emptyMessage={t('members.invitationsEmpty')}
                            actionsLabel={t('common.actions')}
                            actions={(invitation) => (
                                <div className="flex justify-end">
                                    <Button
                                        size="lg"
                                        variant="secondary"
                                        onClick={() => setRevoking(invitation)}
                                    >
                                        {t('members.revoke')}
                                        <span className="sr-only"> {invitation.email}</span>
                                    </Button>
                                </div>
                            )}
                        />
                    )}
                </DialogBox>
            )}

            <MemberAccessModal
                project={project}
                target={accessTarget}
                onClose={() => setAccessTarget(null)}
            />

            <ConfirmActionModal
                opened={!!removing}
                onClose={() => {
                    if (!remove.isPending) setRemoving(null);
                }}
                title={t('members.confirmRemove.title', {
                    name: removing ? memberLabel(removing) : '',
                })}
                message={t('members.confirmRemove.message', { project: project.name })}
                confirmLabel={t('members.confirmRemove.confirm')}
                confirmVariant="danger"
                isLoading={remove.isPending}
                onConfirm={() => removing && remove.mutate(removing)}
            />

            <ConfirmActionModal
                opened={!!revoking}
                onClose={() => {
                    if (!revoke.isPending) setRevoking(null);
                }}
                title={t('members.confirmRevoke.title')}
                message={t('members.confirmRevoke.message', { email: revoking?.email ?? '' })}
                confirmLabel={t('members.confirmRevoke.confirm')}
                confirmVariant="danger"
                isLoading={revoke.isPending}
                onConfirm={() => revoking && revoke.mutate(revoking)}
            />
        </div>
    );
}

export function LoadError({ onRetry }: { onRetry: () => void }) {
    const { t } = useTranslation('projects');
    return (
        <div role="alert" className="w-full p-4 flex flex-wrap items-center gap-3 text-sm">
            <span>{t('common.loadFailed')}</span>
            <Button size="lg" variant="secondary" onClick={onRetry}>
                {t('common.retry')}
            </Button>
        </div>
    );
}
